import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ViewType } from '../../hooks/useNavigation';
import {
  EnvelopeIcon,
  BotIcon,
  TrendingUpIcon,
  CogIcon,
  QuestionMarkCircleIcon,
  WhatsAppIcon,
  PencilSquareIcon,
  UserCheckIcon,
  SparklesIcon,
  DocumentTextIcon, // Zoho
  ClockIcon, // Google Calendar
  UserGroupIcon, // Team Access
  ChartBarIcon, // Campaign History
  FolderIcon,
  LightBulbIcon,
  BuildingOfficeIcon, // CRM Clients
} from '../UI/Icons';
import { useAddonManager } from '../Addons/hooks/useAddonManager';

interface LeftNavigationProps {
  activeView: ViewType;
  onViewChange: (view: ViewType) => void;
  onSettingsClick: () => void;
  logoSrc?: string;
  title?: string;
  subtitle?: string;
}

interface NavigationItem {
  id: ViewType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  action?: () => void;
}

// WhatsApp-specific navigation items (shown when WhatsApp addon is enabled)
const whatsappNavigationItems: NavigationItem[] = [
  {
    id: 'whatsapp' as ViewType,
    label: 'Chatbot',
    icon: BotIcon,
  },
  {
    id: 'ai-persona' as ViewType,
    label: 'AI Persona',
    icon: BotIcon,
  },
  {
    id: 'send-templates' as ViewType,
    label: 'Broadcast',
    icon: EnvelopeIcon,
  },
  {
    id: 'campaign-history' as ViewType,
    label: 'Campaign History',
    icon: ChartBarIcon,
  },
  {
    id: 'templates' as ViewType,
    label: 'Create Template',
    icon: PencilSquareIcon,
  },
  {
    id: 'agent-triggers' as ViewType,
    label: 'Agent Triggers',
    icon: UserCheckIcon,
  },
  {
    id: 'agent-reports' as ViewType,
    label: 'Agent Reports',
    icon: BotIcon,
  },
];

// Zoho CRM navigation items
const zohoNavigationItems: NavigationItem[] = [
  {
    id: 'addons/zoho-crm' as ViewType,
    label: 'Dashboard',
    icon: DocumentTextIcon,
  },
];

// Google Calendar navigation items
const googleCalendarNavigationItems: NavigationItem[] = [
  {
    id: 'addons/google-calendar' as ViewType,
    label: 'Calendar',
    icon: ClockIcon,
  },
];

// CRM navigation items
const leadsNavigationItems: NavigationItem[] = [
  {
    id: 'addons/leads' as ViewType,
    label: 'Leads',
    icon: UserCheckIcon,
  },
  {
    id: 'addons/crm-clients' as ViewType,
    label: 'Clients',
    icon: BuildingOfficeIcon,
  },
];

// General navigation items (always shown)
const generalNavigationItems: NavigationItem[] = [
  {
    id: 'data' as ViewType,
    label: 'Business Data',
    icon: FolderIcon,
  },
  {
    id: 'intelligence' as ViewType,
    label: 'Intelligence',
    icon: LightBulbIcon,
  },
  {
    id: 'analytics' as ViewType,
    label: 'Analytics',
    icon: TrendingUpIcon,
  },
  {
    id: 'team-access' as ViewType,
    label: 'Team Access',
    icon: UserGroupIcon,
  },
  {
    id: 'addons' as ViewType,
    label: 'Add-ons',
    icon: CogIcon,
  },
];

const LeftNavigation: React.FC<LeftNavigationProps> = (props) => {
  const {
    activeView,
    onViewChange,
    onSettingsClick,
  } = props;
  const navigate = useNavigate();
  const { getAddon } = useAddonManager();
  const [isWhatsAppExpanded, setIsWhatsAppExpanded] = useState(true);
  const [isZohoExpanded, setIsZohoExpanded] = useState(true);
  const [isGoogleCalendarExpanded, setIsGoogleCalendarExpanded] = useState(true);
  const [isLeadsExpanded, setIsLeadsExpanded] = useState(true);

  // Check if Addons are enabled and setup complete
  const whatsappAddon = getAddon('whatsapp');
  const isWhatsAppEnabled = whatsappAddon?.isEnabled && whatsappAddon?.setupComplete;

  const zohoAddon = getAddon('zoho_crm');
  const isZohoEnabled = zohoAddon?.isEnabled && zohoAddon?.setupComplete;

  const googleCalendarAddon = getAddon('google_calendar');
  const isGoogleCalendarEnabled = googleCalendarAddon?.isEnabled && googleCalendarAddon?.setupComplete;

  const leadsAddon = getAddon('leads');
  const isLeadsEnabled = leadsAddon?.isEnabled && leadsAddon?.setupComplete;

  const renderNavItem = (item: NavigationItem, indented: boolean = false) => (
    <li key={item.id}>
      <button
        onClick={() => {
          console.log('LeftNavigation click:', item.id);
          onViewChange(item.id);
          try {
            navigate(`/${item.id}`);
          } catch (err) {
            console.warn('LeftNavigation navigate failed', err);
          }
        }}
        className={`
          w-full ${indented ? 'ml-4 mr-2' : 'mx-2'} px-4 py-3 flex items-center justify-start rounded-lg
          transition-all duration-200 ease-in-out text-white
          focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50
          ${activeView === item.id
            ? 'bg-[linear-gradient(90deg,var(--color-primary),#6d28d9)] shadow-lg ring-2 ring-white/10 border-l-4'
            : 'hover:bg-white hover:bg-opacity-10'
          }
        `}
        style={activeView === item.id ? { borderLeftColor: 'var(--color-success)' } : undefined}
        title={item.label}
      >
        <item.icon className="w-6 h-6 flex-shrink-0" />
        <span className="ml-3 font-medium text-sm text-white">
          {item.label}
        </span>
      </button>
    </li>
  );

  return (
    <div className="h-screen flex flex-col bg-sidebar-bg text-white transition-all duration-200">
      {/* Header */}
      <div className="py-4 px-3 border-b border-sidebar-bg/30 mb-4">
        <div className="flex items-center justify-start"> {/* Left aligned */}
          <div className="flex items-center">
             {props.logoSrc ? (
                 <img src={props.logoSrc} alt="Logo" className="w-16 h-16 mr-3 object-contain rounded-md" />
             ) : (
                <SparklesIcon className="w-8 h-8 text-white mr-3 flex-shrink-0" />
             )}
            <div className="flex flex-col min-w-0">
                <h1 className="text-lg font-semibold text-white font-['Poppins'] leading-tight truncate">
                  {props.title || 'AI Suite'}
                </h1>
                {props.subtitle && (
                   <span className="text-xs text-secondary-text opacity-75 truncate">{props.subtitle}</span>
                )}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Items */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden">
        <style>{`
          nav::-webkit-scrollbar {
            display: none;
          }
        `}</style>
        <ul className="space-y-2">
          {/* WhatsApp Section - Only show if enabled */}
          {isWhatsAppEnabled && (
            <>
              {/* WhatsApp Section Header */}
              <li className="px-4">
                <button
                  onClick={() => setIsWhatsAppExpanded(!isWhatsAppExpanded)}
                  className="w-full flex items-center justify-between py-2 text-white/70 hover:text-white transition-colors"
                >
                  <div className="flex items-center">
                    <WhatsAppIcon className="w-5 h-5 mr-2" />
                    <span className="text-xs font-semibold uppercase tracking-wider">WhatsApp</span>
                  </div>
                  <svg
                    className={`w-4 h-4 transition-transform ${isWhatsAppExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </li>

              {/* WhatsApp Navigation Items */}
              {isWhatsAppExpanded && (
                <div className="space-y-1 pb-4">
                  {whatsappNavigationItems.map(item => renderNavItem(item, true))}
                </div>
              )}

              {/* Divider */}
              <li className="px-4 py-2">
                <div className="border-t border-white/10"></div>
              </li>
            </>
          )}

          {/* If WhatsApp is not enabled, show a connect prompt */}
          {!isWhatsAppEnabled && (
            <>
              <li className="px-4 py-2">
                <button
                  onClick={() => {
                    onViewChange('addons' as ViewType);
                    navigate('/addons/whatsapp');
                  }}
                  className="w-full p-4 rounded-lg bg-gradient-to-r from-green-500/20 to-green-600/20 border border-green-500/30 hover:from-green-500/30 hover:to-green-600/30 transition-all"
                >
                  <div className="flex items-center mb-2">
                    <WhatsAppIcon className="w-5 h-5 text-green-400 mr-2" />
                    <span className="text-sm font-semibold text-white">Connect WhatsApp</span>
                  </div>
                  <p className="text-xs text-white/60 text-left">
                    Link your WhatsApp Business Account to unlock AI chatbot, broadcasts, and more
                  </p>
                </button>
              </li>
              <li className="px-4 py-2">
                <div className="border-t border-white/10"></div>
              </li>
            </>
          )}

          {/* Zoho CRM Section - Only show if enabled */}
          {isZohoEnabled && (
            <>
              <li className="px-4">
                <button
                  onClick={() => setIsZohoExpanded(!isZohoExpanded)}
                  className="w-full flex items-center justify-between py-2 text-white/70 hover:text-white transition-colors"
                >
                  <div className="flex items-center">
                    <DocumentTextIcon className="w-5 h-5 mr-2" />
                    <span className="text-xs font-semibold uppercase tracking-wider">Zoho CRM</span>
                  </div>
                  <svg
                    className={`w-4 h-4 transition-transform ${isZohoExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </li>

              {isZohoExpanded && (
                <div className="space-y-1 pb-4">
                  {zohoNavigationItems.map(item => renderNavItem(item, true))}
                </div>
              )}

              <li className="px-4 py-2">
                <div className="border-t border-white/10"></div>
              </li>
            </>
          )}

          {/* Google Calendar Section - Only show if enabled */}
          {isGoogleCalendarEnabled && (
            <>
              <li className="px-4">
                <button
                  onClick={() => setIsGoogleCalendarExpanded(!isGoogleCalendarExpanded)}
                  className="w-full flex items-center justify-between py-2 text-white/70 hover:text-white transition-colors"
                >
                  <div className="flex items-center">
                    <ClockIcon className="w-5 h-5 mr-2" />
                    <span className="text-xs font-semibold uppercase tracking-wider">Google Calendar</span>
                  </div>
                  <svg
                    className={`w-4 h-4 transition-transform ${isGoogleCalendarExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </li>

              {isGoogleCalendarExpanded && (
                <div className="space-y-1 pb-4">
                  {googleCalendarNavigationItems.map(item => renderNavItem(item, true))}
                </div>
              )}

              <li className="px-4 py-2">
                <div className="border-t border-white/10"></div>
              </li>
            </>
          )}

          {/* CRM Section - Only show if enabled */}
          {isLeadsEnabled && (
            <>
              <li className="px-4">
                <button
                  onClick={() => setIsLeadsExpanded(!isLeadsExpanded)}
                  className="w-full flex items-center justify-between py-2 text-white/70 hover:text-white transition-colors"
                >
                  <div className="flex items-center">
                    <UserCheckIcon className="w-5 h-5 mr-2" />
                    <span className="text-xs font-semibold uppercase tracking-wider">CRM</span>
                  </div>
                  <svg
                    className={`w-4 h-4 transition-transform ${isLeadsExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </li>

              {isLeadsExpanded && (
                <div className="space-y-1 pb-4">
                  {leadsNavigationItems.map(item => renderNavItem(item, true))}
                </div>
              )}

              <li className="px-4 py-2">
                <div className="border-t border-white/10"></div>
              </li>
            </>
          )}

          {/* General Navigation Items */}
          <li className="px-4 pt-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-white/50">General</span>
          </li>
          {generalNavigationItems.map(item => renderNavItem(item))}
        </ul>
      </nav>

      {/* Footer */}
      <div className="py-6 px-2 border-t border-sidebar-bg/30">
        {/* Settings */}
        <div className="space-y-4">
          <button
            onClick={onSettingsClick}
            className={`
              w-full mx-2 px-2 py-2 flex items-center justify-start rounded-lg
              transition-all duration-200 ease-in-out text-white
              focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50
              hover:bg-white hover:bg-opacity-10
            `}
            title="Settings"
          >
            <CogIcon className="w-5 h-5 flex-shrink-0" />
            <span className="ml-3 font-medium text-sm text-white">
              Settings
            </span>
          </button>
        </div>

        {/* Help/Support */}
        <div>
          <button
            onClick={() => onViewChange('help' as ViewType)}
            className={`
              w-full mx-2 px-2 py-2 flex items-center justify-start rounded-lg
              transition-all duration-200 ease-in-out text-white
              focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50
              hover:bg-white hover:bg-opacity-10
            `}
            title="Help & Support"
          >
            <QuestionMarkCircleIcon className="w-5 h-5 flex-shrink-0" />
            <span className="ml-3 font-medium text-sm text-white">
              Help & Support
            </span>
          </button>
        </div>

        {/* Version */}
        <div className="mt-6 text-xs text-white/60 text-center px-2">
          v1.0.0
        </div>
      </div>
    </div>
  );
};

export default LeftNavigation;